import {
	type ChangeEvent,
	type ComponentProps,
	type FC,
	useState,
} from "react";
import type { PlacesAutocompleteDto } from "shared";
import { useDebounceCallback, useEventCallback } from "usehooks-ts";
import type { ButtonProps } from "../components/Button";
import SearchBox from "../components/SearchBox";
import { useGooglePlaceAutocomplete } from "../settings/useIntegrations";

interface Props extends Omit<ComponentProps<"div">, "onChange"> {
	onChange: (dto: PlacesAutocompleteDto) => void;
}

export const SearchGooglePlaces: FC<Props> = ({ onChange, ...rest }) => {
	const [query, setQuery] = useState("");
	const {
		data: placesData,
		refetch: refetchPlaces,
		isFetching: placesLoading,
	} = useGooglePlaceAutocomplete(query);

	const debounceAutocomplete = useDebounceCallback(async () => {
		await refetchPlaces();
	}, 500);

	const handlePlacesAutocomplete = useEventCallback(
		async (e: ChangeEvent<HTMLInputElement>) => {
			setQuery(e.target.value);
			if (e.target.value.length > 0) {
				await debounceAutocomplete();
			}
		},
	);

	return (
		<SearchBox
			loading={placesLoading}
			inputProps={{
				placeholder: "Service Area",
				value: query,
				onChange: handlePlacesAutocomplete,
			}}
			autocompleteItems={placesData?.map(
				(place) =>
					({
						full: true,
						icon: "add_location",
						variant: "ghost",
						iconSize: 24,
						children: <p>{place?.placePrediction?.text?.text}</p>,
						onClick: () => {
							onChange(place);
							setQuery("");
						},
					}) as ButtonProps,
			)}
			{...rest}
		/>
	);
};
